import React, { useState, useEffect } from "react";
import { Form, Button, FormControl } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDateTime } from "../../../lib/bands/bandModel";

// Ready-made patterns; "custom" lets the user type their own.
export const DATE_TIME_PRESETS = [
  { pattern: "DD/MM/YYYY HH:mm", label: "05/10/2026 06:18" },
  { pattern: "DD/MM/YYYY HH:mm:ss", label: "05/10/2026 06:18:02" },
  { pattern: "DD/MM/YYYY", label: "05/10/2026" },
  { pattern: "DD MMM YYYY", label: "05 Oct 2026" },
  { pattern: "DD MMM YYYY, h:mm A", label: "05 Oct 2026, 6:18 AM" },
  { pattern: "dddd, D MMMM YYYY", label: "Monday, 5 October 2026" },
  { pattern: "YYYY-MM-DD HH:mm", label: "2026-10-05 06:18" },
  { pattern: "MM/DD/YYYY h:mm A", label: "10/05/2026 6:18 AM" },
  { pattern: "HH:mm", label: "06:18" },
];

const TOKEN_HELP =
  "YYYY 2026 · YY 26 · MMMM October · MMM Oct · MM 10 · DD 05 · D 5 · dddd Monday · ddd Mon · " +
  "HH 18 (24h) · hh 06 / h 6 (12h) · A AM/PM · mm minutes · ss seconds · [text] prints as written";

// Pattern picker with a live example: a preset, or "Custom…" with a pattern
// box. Used by the panels' "Current date & time" tool and the toolbar.
export function DateTimePatternField({ pattern, onChange, size = "sm", compact = false }) {
  const isPreset = DATE_TIME_PRESETS.some((p) => p.pattern === pattern);
  const [custom, setCustom] = useState(!isPreset);
  const [now, setNow] = useState(new Date());

  // Keep the example ticking so seconds/minutes visibly update.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <select
        className={`form-select form-select-${size}${compact ? " toolbar-input" : ""}`}
        value={custom ? "__custom" : pattern}
        onChange={(e) => {
          if (e.target.value === "__custom") {
            setCustom(true);
          } else {
            setCustom(false);
            onChange(e.target.value);
          }
        }}
      >
        {DATE_TIME_PRESETS.map((p) => <option key={p.pattern} value={p.pattern}>{p.label}</option>)}
        <option value="__custom">Custom…</option>
      </select>
      {custom && (
        <FormControl
          size={size}
          className="mt-1"
          value={pattern}
          placeholder="e.g. DD/MM/YYYY [at] HH:mm"
          title={TOKEN_HELP}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {!compact && (
        <Form.Text className="text-muted d-block">
          Prints like: <strong>{formatDateTime(now, pattern) || "—"}</strong>
          {custom && <span className="d-block small mt-1">{TOKEN_HELP}</span>}
        </Form.Text>
      )}
    </>
  );
}

// "Current date & time" in a section's panel: choose a format and add an
// element that the PDF fills with the date and time it is printed.
export default function DateTimeTool({ onAdd }) {
  const [pattern, setPattern] = useState(DATE_TIME_PRESETS[0].pattern);

  return (
    <div className="date-time-tool border-bottom pb-3 mb-3">
      <Form.Label className="fw-bold mb-1">Current date &amp; time</Form.Label>
      <Form.Text className="text-muted d-block mb-2">
        Prints the date and time the document is printed, in the format you choose.
      </Form.Text>
      <DateTimePatternField pattern={pattern} onChange={setPattern} />
      <Button size="sm" variant="outline-primary" className="mt-2" disabled={!pattern.trim()} onClick={() => onAdd(pattern)}>
        <FontAwesomeIcon icon="clock" className="me-1" />
        Add to this section
      </Button>
    </div>
  );
}
